export const MAX_IMPORT_BYTES = 1024 * 1024
export const MAX_IMPORT_ROWS = 500

// Deliberately separate from the account-statement/trade importer. CSV cells
// are text only; never interpret spreadsheet formulas or infer instruments.
export function parseAssetListCsv(text) {
  if (typeof text !== 'string' || text.length > MAX_IMPORT_BYTES) throw new Error('Choose a CSV smaller than 1 MB.')
  const records = []
  let record = [], cell = '', quoted = false, closed = false
  const source = text.replace(/^\uFEFF/, '')
  const endCell = () => { record.push(cell.trim()); cell = ''; closed = false }
  const endRecord = () => {
    endCell()
    if (record.some(Boolean)) records.push(record)
    record = []
    if (records.length > MAX_IMPORT_ROWS + 1) throw new Error('Import up to 500 rows at a time.')
  }
  for (let i = 0; i < source.length; i += 1) {
    const char = source[i]
    if (quoted) {
      if (char === '"' && source[i + 1] === '"') { cell += '"'; i += 1 }
      else if (char === '"') { quoted = false; closed = true }
      else cell += char
    } else if (char === ',') endCell()
    else if (char === '\n' || char === '\r') {
      endRecord()
      if (char === '\r' && source[i + 1] === '\n') i += 1
    } else if (char === '"') {
      if (cell.trim() || closed) throw new Error('Malformed CSV quote.')
      cell = ''; quoted = true
    } else {
      if (closed && char.trim()) throw new Error('Unexpected text after CSV quote.')
      cell += char
    }
  }
  if (quoted) throw new Error('Unclosed CSV quote.')
  endRecord()
  if (!records.length) throw new Error('The CSV is empty.')
  const columns = records[0].map((value, index) => /^(ticker|symbol)$/i.test(value) ? index : -1).filter(index => index >= 0)
  if (columns.length > 1 || (!columns.length && records.some(row => row.length !== 1))) {
    throw new Error('Use one Ticker or Symbol column, or a single column without a header.')
  }
  const column = columns[0] ?? 0
  const values = columns.length ? records.slice(1) : records
  if (!values.length) throw new Error('The CSV has no tickers.')
  if (values.length > MAX_IMPORT_ROWS) throw new Error('Import up to 500 rows at a time.')
  const seen = new Set()
  return values.map((record, index) => {
    const symbol = String(record[column] || '').trim().toUpperCase()
    let status = 'queued', message = 'Ready to import'
    if (!/^[A-Z][A-Z0-9.\-]{0,23}$/.test(symbol)) {
      status = 'invalid'; message = 'Invalid or missing ticker'
    } else if (seen.has(symbol)) {
      status = 'duplicate'; message = 'Duplicate in CSV — skipped'
    }
    seen.add(symbol)
    return { id: index, symbol, status, message }
  })
}

export function importQuoteStatus(snapshot) {
  if (snapshot?.freshness === 'unavailable') return 'Data unavailable'
  if (snapshot?.freshness === 'stale') return 'Stale data — reconnecting'
  const hasPrice = ['last', 'bid', 'ask'].some(field => Number.isFinite(snapshot?.[field]) && snapshot[field] > 0)
  if (!hasPrice) return 'Waiting for quote'
  if (snapshot.freshness === 'live') return 'Live data'
  if (snapshot.freshness === 'delayed') return 'Delayed data'
  return 'Waiting for quote'
}

function memberFor(rows, candidate) {
  return rows.find(row => Number(row.conid ?? row.contract?.conid ?? row.providerMapping?.conid) === Number(candidate.conid))
}

// The deployed view returns canonical instrument IDs, not provider mappings.
// A symbol fallback is allowed only AFTER the server confirms membership for
// the exact verified contract. Never use it to skip verification before add.
function confirmedMemberFor(rows, candidate) {
  const exact = memberFor(rows, candidate)
  if (exact) return exact
  const matches = rows.filter(row => row.instrumentId
    && ['equity', 'etf', 'stock'].includes(row.assetType)
    && String(row.symbol || '').trim().toUpperCase() === candidate.symbol
    && !(row.conid ?? row.contract?.conid ?? row.providerMapping?.conid))
  return matches.length === 1 ? matches[0] : null
}

// One verified add at a time: preserve list-version checks, canonical identity
// and durable subscription registration owned by the existing server command.
export async function importAssetListRows({ rows, listId, api, onRow = () => {}, signal }) {
  for (const row of rows) {
    if (signal?.aborted) break
    if (!['queued', 'error'].includes(row.status)) continue
    let metadata = {}
    const publish = patch => onRow({ ...row, ...metadata, ...patch })
    try {
      publish({ status: 'resolving', message: 'Finding exact stock or ETF' })
      const response = await api.search(row.symbol, { signal })
      if (signal?.aborted) break
      const matches = [...new Map((response.results || [])
        .filter(candidate => candidate.symbol === row.symbol && candidate.secType === 'STK'
          && candidate.exchange === 'SMART' && candidate.currency === 'USD'
          && Number.isSafeInteger(Number(candidate.conid)) && Number(candidate.conid) > 0)
        .map(candidate => [Number(candidate.conid), candidate])).values()]
      if (matches.length !== 1) throw new Error(matches.length ? 'Ambiguous ticker — choose the contract with Add Asset.' : 'No exact U.S. stock or ETF found.')
      const candidate = matches[0]
      metadata = { companyName: candidate.companyName || candidate.description || '', sector: candidate.sector || '', sectorSource: candidate.sectorSource || '', category: candidate.category || '', subcategory: candidate.subcategory || '' }
      let member, savedInstrumentId = null, added = false, existing = false
      for (let attempt = 0; attempt < 2; attempt += 1) {
        const state = await api.readState()
        const list = state.lists.find(item => item.listId === listId)
        if (!list) throw new Error('The destination list no longer exists.')
        member = memberFor(await api.readView({ kind: 'manual-list', listId }), candidate)
        if (signal?.aborted) break
        if (member) break
        publish({ status: 'subscribing', message: 'Verifying contract and requesting subscription' })
        try {
          const result = await api.add(listId, candidate, Number(list.version))
          savedInstrumentId = result?.instrumentId || null
          added = true
          break
        } catch (error) {
          if (error.code === 'ASSET_LIST_ALREADY_MEMBER') {
            existing = true
            try { member = confirmedMemberFor(await api.readView({ kind: 'manual-list', listId }), candidate) }
            catch { /* Server confirmed membership; quote observation remains unavailable. */ }
            break
          }
          if (attempt === 0 && error.code === 'ASSET_LIST_VERSION_CONFLICT') continue
          throw error
        }
      }
      if (signal?.aborted && !added && !existing && !member) break
      if (!member && !savedInstrumentId && !existing) {
        try { member = memberFor(await api.readView({ kind: 'manual-list', listId }), candidate) } catch { /* Saved command already succeeded. */ }
      }
      publish({ status: added ? 'added' : 'existing', instrumentId: savedInstrumentId || member?.instrumentId || null,
        message: added ? 'Added to list' : 'Already in list — reused' })
    } catch (error) {
      if (signal?.aborted) break
      publish({ status: 'error', message: error?.message || 'Could not import ticker. Retry to check saved membership.' })
    }
  }
}
